import { Box, Paper, Typography, TextField, useTheme, CircularProgress } from '@mui/material'
import { Button } from '@ds/components'
import { useForgotPassword } from '@refinedev/core'
import { HlLogo } from '@ds/icons'
import { useNavigate } from 'react-router'
import { useForm, Controller } from 'react-hook-form'

type TForgotPasswordFormData = {
  email: string
}

export const ForgotPassword = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { mutate: forgotPassword, isLoading } = useForgotPassword()

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<TForgotPasswordFormData>({
    mode: 'onChange',
    defaultValues: {
      email: '',
    },
  })

  const onSubmit = (data: TForgotPasswordFormData) => {
    forgotPassword({ email: data.email })
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.paper', position: 'relative' }}>
      <Box
        sx={{
          width: '100%',
          position: 'absolute',
          top: 'min(6rem, 10vh)',
          left: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
            '& svg': {
              width: 32,
              height: 32,
            },
          }}
        >
          <HlLogo />
        </Box>
        <Typography variant="subtitle1" color="text.primary">
          Licensing Management Platform
        </Typography>
      </Box>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pt: { xs: 12, sm: 8, md: 6 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            minWidth: { xs: '90%', sm: 400, md: 480 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            bgcolor: 'background.default',
          }}
        >
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 500 }}>
            Forgot Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Enter your email address and we&apos;ll send you a link to reset your password.
          </Typography>

          <Box component="form" sx={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  variant="outlined"
                  type="email"
                  fullWidth
                  sx={{
                    mb: 3,
                    '& .MuiFormHelperText-root': {
                      minHeight: '20px',
                      margin: '4px 0 0 0',
                    },
                  }}
                  autoComplete="email"
                  disabled={isLoading}
                  error={!!errors.email}
                  helperText={errors.email?.message || ' '}
                />
              )}
            />

            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                sx={{ py: 1.5 }}
                disabled={isLoading || !isValid}
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {isLoading ? 'Sending...' : 'Send Reset Email'}
              </Button>

              <Button
                variant="text"
                color="primary"
                onClick={handleBackToLogin}
                disabled={isLoading}
                sx={{ color: theme.palette.text.primary }}
              >
                Back to Login
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}
